/**
 * Life Setup Prompt — First-time setup for birth date and life expectancy.
 * Premium glass design with motivational messaging.
 */

import { memo, useState } from 'react'
import { motion } from 'framer-motion'
import { CalendarHeart } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useUpdateLifeSettings } from '@turnifly/shared/hooks'

export const LifeSetupPrompt = memo(function LifeSetupPrompt() {
  const { t } = useTranslation()
  const [birthDate, setBirthDate] = useState('')
  const [lifeExpectancy, setLifeExpectancy] = useState(80)
  const { mutate: save, isPending } = useUpdateLifeSettings()

  const today = new Date().toISOString().split('T')[0]

  const handleSave = () => {
    if (!birthDate) return
    save({ birth_date: birthDate, life_expectancy: lifeExpectancy })
  }

  return (
    <div className="flex flex-col items-center justify-center min-h-[60vh] px-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="relative w-full max-w-sm overflow-hidden rounded-2xl border border-white/[0.06] bg-white/[0.03]"
      >
        {/* Decorative orbs */}
        <div className="absolute -top-24 -right-20 w-48 h-48 rounded-full bg-primary/10 blur-[80px] pointer-events-none" />
        <div className="absolute -bottom-20 -left-16 w-40 h-40 rounded-full bg-accent/8 blur-[60px] pointer-events-none" />

        <div className="relative p-6 space-y-6">
          {/* Icon */}
          <div className="flex justify-center">
            <motion.div
              animate={{ scale: [1, 1.05, 1] }}
              transition={{ duration: 3, repeat: Infinity }}
              className="flex items-center justify-center w-16 h-16 rounded-2xl bg-primary/10 border border-primary/20"
            >
              <CalendarHeart className="w-8 h-8 text-primary" />
            </motion.div>
          </div>

          {/* Title */}
          <div className="text-center">
            <h2 className="text-lg font-display font-bold text-text">
              {t('life.setupTitle')}
            </h2>
            <p className="text-xs text-hint mt-2 leading-relaxed">
              {t('life.setupDescription')}
            </p>
          </div>

          {/* Birth date input */}
          <div>
            <label className="text-[11px] font-semibold text-hint uppercase tracking-wider">
              {t('life.birthDate')}
            </label>
            <input
              type="date"
              value={birthDate}
              onChange={(e) => setBirthDate(e.target.value)}
              min="1930-01-01"
              max={today}
              className="w-full mt-1.5 px-4 py-3 rounded-xl bg-white/[0.04] border border-white/[0.08] text-text text-sm focus:ring-2 focus:ring-primary/20 focus:border-primary/50 outline-none transition-all"
            />
          </div>

          {/* Life expectancy */}
          <div>
            <div className="flex items-center justify-between">
              <label className="text-[11px] font-semibold text-hint uppercase tracking-wider">
                {t('life.lifeExpectancy')}
              </label>
              <span className="text-sm font-display font-bold text-primary">{lifeExpectancy}</span>
            </div>
            <input
              type="range"
              min={50}
              max={120}
              value={lifeExpectancy}
              onChange={(e) => setLifeExpectancy(Number(e.target.value))}
              className="w-full mt-2 accent-primary"
            />
            <div className="flex justify-between mt-1">
              <span className="text-[9px] text-hint">50</span>
              <span className="text-[9px] text-hint">120</span>
            </div>
          </div>

          {/* Disclaimer */}
          <p className="text-[10px] text-hint/60 text-center leading-relaxed">
            {t('life.setupDisclaimer')}
          </p>

          {/* Save button */}
          <motion.button
            whileTap={{ scale: 0.96 }}
            onClick={handleSave}
            disabled={!birthDate || isPending}
            className="w-full py-3.5 rounded-2xl bg-gradient-to-tr from-primary to-accent text-white font-semibold text-sm shadow-neon disabled:opacity-50 tap-highlight-transparent"
          >
            {isPending ? t('common.saving') : t('life.startTracking')}
          </motion.button>
        </div>
      </motion.div>
    </div>
  )
})
